import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import { LureImage } from '../components/GameImages';
import '../css.css';

const LureModal = React.memo(({ lure, onClose }) => {
  if (!lure) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        
        <div className="lure-detail">
          <div className="lure-main">
            <LureImage 
              filename={lure.filename}
              alt={lure.name}
              className="lure-detail-image"
              style={{ 
                width: '200px', 
                height: '200px', 
                objectFit: 'contain'
              }}
              onError={() => {
                console.warn('Failed to load lure image:', lure.filename);
              }}
            />
            <h2>{lure.name}</h2>
            {lure.description && <p>{lure.description}</p>}
          </div>
          
          <div className="lure-recipe">
            <h3>제작 재료</h3>
            
            {lure.tools && lure.tools.length > 0 && (
              <div className="recipe-section">
                <h4>필요 도구</h4>
                <div className="recipe-items">
                  {lure.tools.map((tool, idx) => (
                    <div key={idx} className="recipe-item">
                      <LureImage 
                        filename={tool.filename}
                        alt={tool.name}
                        className="recipe-item-image"
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          objectFit: 'contain'
                        }}
                        onError={() => {
                          console.warn('Failed to load lure tool image:', tool.filename);
                        }}
                      />
                      <span className="recipe-item-name">{tool.name}</span>
                      {tool.quantity > 1 && <span className="recipe-quantity">x{tool.quantity}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {lure.materials && lure.materials.length > 0 && (
              <div className="recipe-section">
                <h4>필요 재료</h4>
                <div className="recipe-items">
                  {lure.materials.map((material, idx) => (
                    <div key={idx} className="recipe-item">
                      <LureImage 
                        filename={material.filename}
                        alt={material.name}
                        className="recipe-item-image"
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          objectFit: 'contain'
                        }}
                        onError={() => {
                          console.warn('Failed to load lure material image:', material.filename);
                        }}
                      />
                      <span className="recipe-item-name">{material.name}</span>
                      {material.quantity > 1 && <span className="recipe-quantity">x{material.quantity}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const Page1 = React.memo(() => {
  const [lureCategories, setLureCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedLure, setSelectedLure] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/api/lure');
        if (!response.ok) throw new Error('Network response was not ok');
        
        const data = await response.json();
        
        // API가 이미 카테고리별로 분류된 객체를 반환하는 경우
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          setLureCategories(data);
        } else if (Array.isArray(data)) {
          // API가 배열을 반환하는 경우 카테고리별로 분류
          const categorizedData = data.reduce((acc, lure) => {
            const category = lure.category || '기타';
            if (!acc[category]) {
              acc[category] = [];
            }
            acc[category].push(lure);
            return acc;
          }, {});
          setLureCategories(categorizedData);
        } else {
          // 예상치 못한 데이터 형태
          console.error('Unexpected data format:', data);
          setLureCategories({});
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching lure data:', error);
        setLureCategories({});
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 루어 클릭 핸들러를 useCallback으로 최적화
  const handleLureClick = useCallback(async (lureId) => {
    setModalLoading(true);
    
    try {
      const response = await fetch(`/api/lure/${lureId}`);
      if (!response.ok) throw new Error('Network response was not ok');
      
      const data = await response.json();
      setSelectedLure(data);
    } catch (error) {
      console.error('Error fetching lure details:', error);
    } finally {
      setModalLoading(false);
    }
  }, []);

  // 모달 닫기 핸들러를 useCallback으로 최적화
  const handleCloseModal = useCallback(() => {
    setSelectedLure(null);
  }, []);

  // 루어 카테고리 렌더링을 useMemo로 최적화
  const renderedLureCategories = useMemo(() => {
    if (Object.keys(lureCategories).length === 0) {
      return (
        <div className="no-data">
          <p>루어 데이터가 없습니다. 데이터베이스를 확인해주세요.</p>
        </div>
      );
    }

    return Object.entries(lureCategories).map(([category, lures]) => (
      <div key={category} className="category-section">
        <h2 className="category-title">{category}</h2>
        <div className="lure-grid">
          {lures.map((lure) => (
            <div key={lure.id} className="lure-tile" onClick={() => handleLureClick(lure.id)}>
              <LureImage 
                filename={lure.filename}
                alt={lure.name}
                className="lure-tile-image"
                style={{ 
                  width: '120px', 
                  height: '120px', 
                  objectFit: 'contain'
                }}
                onError={() => {
                  console.warn('Failed to load lure image:', lure.filename);
                }}
              />
              <div className="lure-tile-name">{lure.name}</div>
            </div>
          ))}
        </div>
      </div>
    ));
  }, [lureCategories, handleLureClick]);

  return (
    <>
      <h1 className="page-title">루어 정보</h1>
      <div>
        {loading ? (
          <div className="spinner-container">
            <LoadingSpinner
              height={80}
              width={80}
              color="#4fa94d"
              visible={true}
            />
          </div>
        ) : (
          <div className="page-content">
            {renderedLureCategories}
          </div>
        )}
        
        {selectedLure && (
          <LureModal 
            lure={selectedLure} 
            onClose={handleCloseModal} 
          />
        )}
        
        {modalLoading && (
          <div className="modal-overlay">
            <div className="spinner-container">
              <LoadingSpinner
                height={80}
                width={80}
                color="#4fa94d"
                visible={true}
              />
            </div>
          </div>
        )}
        
        <Comments pageName="lure" />
      </div>
    </>
  );
});

export default Page1;
